import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteUserAccount } from '../../components/apiClient.js';
import ProfileNavigation from './ProfileNavigation';

const AccountSettings: React.FC = () => {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [reason, setReason] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);

    const handleDeleteClick = () => {
        setError(null);
        setPasswordError(null);
        setShowDeleteModal(true);
    };

    const handleModalClose = () => {
        setShowDeleteModal(false);
        setShowConfirmModal(false);
        setPassword('');
        setReason('');
        setPasswordError(null);
        setError(null);
    };

    const handleConfirmDelete = () => {
        // Validate password
        if (!password || password.trim().length === 0) {
            setPasswordError('Password is required to delete your account');
            return;
        }

        setShowDeleteModal(false);
        setShowConfirmModal(true);
    };

    const handleFinalDelete = async () => {
        setIsDeleting(true);
        setError(null);

        try {
            const response = await deleteUserAccount(password, reason || undefined);
            
            if (response.status === 'success') {
                // Show success message and redirect
                alert('Your account deletion request has been submitted. You will receive an email confirmation. You will now be logged out.');
                // Redirect to home page (logout is handled by apiClient)
                navigate('/');
            } else {
                setError(response.message || 'Failed to delete account. Please try again.');
                setShowConfirmModal(false);
                setShowDeleteModal(true);
            }
        } catch (err: any) {
            console.error('Delete account error:', err);
            setError(err.message || 'An error occurred. Please try again.');
            setShowConfirmModal(false);
            setShowDeleteModal(true);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    <h2 className="mb-4">
                        <i className="fas fa-cog me-2"></i>
                        Account Settings
                    </h2>
                </div>
            </div>

            <ProfileNavigation />

            <div className="row mt-4">
                <div className="col-lg-8">
                    {/* Account Security Section */}
                    <div className="card mb-4 shadow-sm">
                        <div className="card-header bg-primary text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-shield-alt me-2"></i>
                                Account Security
                            </h5>
                        </div>
                        <div className="card-body">
                            <p className="text-muted">
                                Manage your account security settings and password.
                            </p>
                            <button className="btn btn-outline-primary">
                                <i className="fas fa-key me-2"></i>
                                Change Password
                            </button>
                        </div>
                    </div>

                    {/* Danger Zone - Delete Account */}
                    <div className="card border-danger shadow-sm">
                        <div className="card-header bg-danger text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                Danger Zone
                            </h5>
                        </div>
                        <div className="card-body">
                            <h6 className="text-danger fw-bold">Delete Your Account</h6>
                            <p className="text-muted">
                                Once you delete your account, there is a 30-day grace period before permanent deletion.
                                During this time, you can contact support to restore your account.
                            </p>

                            <div className="alert alert-warning" role="alert">
                                <h6 className="alert-heading">
                                    <i className="fas fa-info-circle me-2"></i>
                                    What happens when you delete your account?
                                </h6>
                                <ul className="mb-0">
                                    <li>You will be immediately logged out</li>
                                    <li>You cannot login to your account anymore</li>
                                    <li>Your account will be marked for deletion</li>
                                    <li>You will receive an email confirmation</li>
                                    <li>After 30 days, an admin will permanently delete all your data including:
                                        <ul>
                                            <li>Profile and study preferences</li>
                                            <li>Quiz history and performance analytics</li>
                                            <li>Points and achievements</li>
                                            <li>Subscription information</li>
                                        </ul>
                                    </li>
                                    <li><strong>To restore your account within 30 days, contact support@wiziai.com</strong></li>
                                </ul>
                            </div>

                            <button 
                                className="btn btn-danger"
                                onClick={handleDeleteClick}
                            >
                                <i className="fas fa-trash-alt me-2"></i>
                                Delete My Account
                            </button>
                        </div>
                    </div>
                </div>

                {/* Help Section */}
                <div className="col-lg-4">
                    <div className="card shadow-sm">
                        <div className="card-header bg-info text-white">
                            <h6 className="mb-0">
                                <i className="fas fa-question-circle me-2"></i>
                                Need Help?
                            </h6>
                        </div>
                        <div className="card-body">
                            <p className="small">
                                If you're having issues with your account or need assistance, 
                                please contact our support team before deleting your account.
                            </p>
                            <p className="small mb-2">
                                <i className="fas fa-envelope me-2"></i>
                                <strong>Email:</strong> support@wiziai.com
                            </p>
                            <p className="small mb-0">
                                <i className="fas fa-clock me-2"></i>
                                <strong>Response Time:</strong> Within 24 hours
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal - Step 1 */}
            {showDeleteModal && (
                <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Confirm Account Deletion
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white" 
                                    onClick={handleModalClose}
                                    disabled={isDeleting}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="fw-bold text-danger">
                                    Are you sure you want to delete your account?
                                </p>
                                <p>Please enter your password to confirm:</p>
                                
                                <div className="mb-3">
                                    <label htmlFor="password" className="form-label">
                                        Password <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                                        id="password"
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            setPasswordError(null);
                                        }}
                                        placeholder="Enter your password"
                                        disabled={isDeleting}
                                    />
                                    {passwordError && (
                                        <div className="invalid-feedback">{passwordError}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="reason" className="form-label">
                                        Reason for leaving (optional)
                                    </label>
                                    <textarea
                                        className="form-control"
                                        id="reason"
                                        rows={3}
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="Help us improve by telling us why you're leaving..."
                                        disabled={isDeleting}
                                    />
                                </div>

                                {error && (
                                    <div className="alert alert-danger" role="alert">
                                        <i className="fas fa-exclamation-circle me-2"></i>
                                        {error}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={handleModalClose}
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger"
                                    onClick={handleConfirmDelete}
                                    disabled={isDeleting}
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Final Confirmation Modal - Step 2 */}
            {showConfirmModal && (
                <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Final Confirmation
                                </h5>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-danger" role="alert">
                                    <h6 className="alert-heading fw-bold">
                                        ⚠️ This action cannot be easily undone!
                                    </h6>
                                    <p className="mb-0">
                                        Your account will be marked for deletion and you will be logged out immediately.
                                        You will have 30 days to contact support to restore your account.
                                    </p>
                                </div>

                                <p className="fw-bold">Are you absolutely sure you want to proceed?</p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={handleModalClose}
                                    disabled={isDeleting}
                                >
                                    Cancel - Keep My Account
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger"
                                    onClick={handleFinalDelete}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-trash-alt me-2"></i>
                                            Yes, Delete My Account
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountSettings;
